// src/components/LocationSelect/LocationModal.jsx
import { useState, useEffect, useCallback } from '@wordpress/element';
import { useConfig } from '../../contexts/ConfigContext';
import * as storage from '../../browserStorage';
import LocationGate from './LocationGate';
import AgeGate from './AgeGate';

const LocationModal = ({
    retailers,
    retailerId,
    onRetailerSelect,
    onAgeGatePass,
    showLocationGate,
    showAgeGate,
    nonHeadlessRetailer,
    setNonHeadlessRetailer,
    nonHeadlessRetailerData,
    setNonHeadlessRetailerData
}) => {
    const { locationGateConfig, allRetailersConfig } = useConfig();
    
    const [currentView, setCurrentView] = useState(() => {
        if (showLocationGate) return 'location';
        if (showAgeGate) return 'age';
        return null;
    });
    
    const [selectedRetailer, setSelectedRetailer] = useState(null);
    const [opacity, setOpacity] = useState(1);
    const [ageGateType, setAgeGateType] = useState(null);
    
    // Fade animation functions
    const fadeAnimation = useCallback((startOpacity, endOpacity, callback) => {
        let op = startOpacity;
        const fade = () => {
            if ((startOpacity < endOpacity && op >= endOpacity) || 
                (startOpacity > endOpacity && op <= endOpacity)) {
                clearInterval(interval);
                setOpacity(endOpacity);
                if (callback) callback();
            } else {
                op += (endOpacity - startOpacity) > 0 ? 0.15 : -0.15;
                setOpacity(op);
            }
        };
        const interval = setInterval(fade, 20);
    }, []);
    
    const fadeOutLocationGate = useCallback((callback) => 
        fadeAnimation(1, 0, callback), [fadeAnimation]);
    const fadeInAgeGate = useCallback(() => 
        fadeAnimation(0, 1), [fadeAnimation]);
    
    // Handle location selection
    const handleLocationSelect = useCallback((retailer) => {
        setSelectedRetailer(retailer);
        
        if (retailer.id) {
            // Set age gate type
            setAgeGateType(retailer.ageGateType || locationGateConfig.ageGateType);
            
            // Update retailer in context
            onRetailerSelect(retailer.id);
            
            // If age gate is needed, transition to it
            if (showAgeGate || (locationGateConfig?.enableAgeGate && 
                storage.getItem('HEADLESS_AGE_GATE_PASSED') !== 'true')) {
                fadeOutLocationGate(() => {
                    setCurrentView('age');
                    fadeInAgeGate();
                });
            }
            // If no age gate needed, we're done (context handles navigation)
        } else if (retailer.non_headless_location_homepage) {
            // Handle non-headless retailer
            storage.setItem('NON_HEADLESS_RETAILER', retailer.location_name);
            setNonHeadlessRetailerData(retailer);
            window.location.href = retailer.non_headless_location_homepage;
        }
    }, [locationGateConfig, onRetailerSelect, setNonHeadlessRetailerData, 
        showAgeGate, fadeOutLocationGate, fadeInAgeGate]);
    
    // Handle age gate completion
    const handleAgeGatePass = useCallback(() => {
        onAgeGatePass();
        // Navigation is handled by context after age gate passes
    }, [onAgeGatePass]);
    
    // Update age gate type when retailer changes
    useEffect(() => {
        if (selectedRetailer) {
            setAgeGateType(selectedRetailer.ageGateType || locationGateConfig.ageGateType);
        } else if (retailerId && retailers[retailerId]) {
            setAgeGateType(retailers[retailerId].ageGateType || locationGateConfig.ageGateType);
        }
    }, [selectedRetailer, retailerId, retailers, locationGateConfig]);
    
    // Helper function to render HTML
    const renderHtml = html => ({ __html: html });
    
    if (!currentView) return null;
    
    return (
        <div
            className={`headless-location-modal-overlay hlm-visible`}
            style={locationGateConfig?.locationGateBgImage ? 
                { backgroundImage: `url(${locationGateConfig.locationGateBgImage})` } : {}
            }
        >
            {locationGateConfig?.locationGateBgVideo && (
                <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    poster={locationGateConfig.locationGateBgImage}
                    style={{ 
                        position: 'fixed', 
                        width: '100%', 
                        height: '100%', 
                        top: '0', 
                        left: '0', 
                        objectFit: 'cover' 
                    }}
                >
                    <source src={locationGateConfig.locationGateBgVideo} type="video/mp4" />
                </video>
            )}
            
            <div className='headless-location-modal-content'>
                <div style={{ opacity }}>
                    {currentView === 'location' && (
                        <LocationGate
                            locationGateConfig={locationGateConfig}
                            renderHtml={renderHtml}
                            allRetailersConfig={allRetailersConfig}
                            retailers={retailers}
                            nonHeadlessRetailers={nonHeadlessRetailer}
                            styleOptions={{}} // Add if needed
                            handleLocationSelect={handleLocationSelect}
                            customRetailersOrder={allRetailersConfig?.customRetailersOrder}
                        />
                    )}
                    
                    {currentView === 'age' && (
                        <AgeGate
                            locationGateConfig={locationGateConfig}
                            renderHtml={renderHtml}
                            handleAgeGate={handleAgeGatePass}
                            storedRetailer={selectedRetailer}
                            retailers={retailers}
                            retailerId={retailerId}
                            setAgeGateType={setAgeGateType}
                            ageGateType={ageGateType}
                            nonHeadlessRetailer={nonHeadlessRetailer}
                            nonHeadlessRetailerData={nonHeadlessRetailerData}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default LocationModal;
